/**
 * Home helper utilities
 * Pure functions — no React dependencies.
 */
import { AlertCircle, CheckCircle, Copy01, Database02, File06, GitMerge, Loading01, ShieldZap, XCircle } from "@untitledui/icons";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import type { HomeDocument, HomeDocumentStatus } from "@/types/home";

// ---------------------------------------------------------------------------
// Status → Badge color
// ---------------------------------------------------------------------------

const STATUS_BADGE_COLOR_MAP: Record<HomeDocumentStatus, BadgeColors> = {
    ready: "success",
    review_required: "warning",
    processing: "brand",
    reading: "brand",
    detecting_type: "brand",
    finding_vendor: "brand",
    finding_receipts: "brand",
    uploading: "brand",
    queued: "gray",
    matching: "blue",
    error: "error",
    alert: "error",
    mismatch: "pink",
    duplicate: "purple",
    exported: "gray",
};

export function getStatusBadgeColor(status: HomeDocumentStatus): BadgeColors {
    return STATUS_BADGE_COLOR_MAP[status] ?? "gray";
}

// ---------------------------------------------------------------------------
// Status → Human-readable label
// ---------------------------------------------------------------------------

const STATUS_LABEL_MAP: Record<HomeDocumentStatus, string> = {
    uploading: "Uploading",
    queued: "Queued",
    processing: "Processing",
    reading: "Reading",
    detecting_type: "Detecting Type",
    finding_vendor: "Finding Vendor",
    finding_receipts: "Finding Receipts",
    ready: "Ready",
    review_required: "Needs Review",
    matching: "Matching",
    alert: "Alert",
    mismatch: "Mismatch",
    duplicate: "Duplicate",
    exported: "Exported",
    error: "Error",
};

export function getStatusLabel(status: HomeDocumentStatus): string {
    return STATUS_LABEL_MAP[status] ?? status;
}

// ---------------------------------------------------------------------------
// Status → Icon config (for DocumentThumbnail)
// ---------------------------------------------------------------------------

type IconComponent = React.FunctionComponent<{ className?: string }>;

export interface StatusIconConfig {
    icon: IconComponent;
    bgClass: string;
    iconClass: string;
    animate: boolean;
}

const STATUS_ICON_MAP: Record<HomeDocumentStatus, StatusIconConfig> = {
    uploading: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    queued: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    processing: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    reading: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    detecting_type: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    finding_vendor: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    finding_receipts: {
        icon: Loading01,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    ready: {
        icon: CheckCircle,
        bgClass: "bg-success-primary",
        iconClass: "fg-success-primary",
        animate: false,
    },
    review_required: {
        icon: AlertCircle,
        bgClass: "bg-warning-primary",
        iconClass: "fg-warning-primary",
        animate: false,
    },
    matching: {
        icon: Database02,
        bgClass: "bg-brand-primary",
        iconClass: "fg-brand-primary",
        animate: true,
    },
    alert: {
        icon: ShieldZap,
        bgClass: "bg-error-secondary",
        iconClass: "fg-error-primary",
        animate: false,
    },
    mismatch: {
        icon: GitMerge,
        bgClass: "bg-[#fff1f3]",
        iconClass: "text-[#e31b54]",
        animate: false,
    },
    duplicate: {
        icon: Copy01,
        bgClass: "bg-[#f4f3ff]",
        iconClass: "text-[#7839ee]",
        animate: false,
    },
    exported: {
        icon: File06,
        bgClass: "bg-secondary",
        iconClass: "fg-tertiary",
        animate: false,
    },
    error: {
        icon: XCircle,
        bgClass: "bg-error-primary",
        iconClass: "fg-error-primary",
        animate: false,
    },
};

export function getStatusIconConfig(status: HomeDocumentStatus): StatusIconConfig {
    return STATUS_ICON_MAP[status] ?? STATUS_ICON_MAP.processing;
}

// ---------------------------------------------------------------------------
// Consolidated STATUS_MAP (for document-list-item, document-thumbnail, etc.)
// ---------------------------------------------------------------------------

export interface StatusMapEntry {
    label: string;
    badgeColor: BadgeColors;
    icon: IconComponent;
    animate: boolean;
}

export const STATUS_MAP: Record<HomeDocumentStatus, StatusMapEntry> = {
    uploading: { label: "Uploading", badgeColor: "brand", icon: Loading01, animate: true },
    queued: { label: "Queued", badgeColor: "brand", icon: Loading01, animate: true },
    processing: { label: "Processing", badgeColor: "brand", icon: Loading01, animate: true },
    reading: { label: "Reading", badgeColor: "brand", icon: Loading01, animate: true },
    detecting_type: { label: "Detecting Type", badgeColor: "brand", icon: Loading01, animate: true },
    finding_vendor: { label: "Finding Vendor", badgeColor: "brand", icon: Loading01, animate: true },
    finding_receipts: { label: "Finding Receipts", badgeColor: "brand", icon: Loading01, animate: true },
    ready: { label: "Ready", badgeColor: "success", icon: CheckCircle, animate: false },
    review_required: { label: "Needs Review", badgeColor: "warning", icon: AlertCircle, animate: false },
    matching: { label: "Matching", badgeColor: "blue", icon: Database02, animate: false },
    alert: { label: "Alert", badgeColor: "error", icon: ShieldZap, animate: false },
    mismatch: { label: "Mismatch", badgeColor: "pink", icon: GitMerge, animate: false },
    duplicate: { label: "Duplicate", badgeColor: "purple", icon: Copy01, animate: false },
    exported: { label: "Exported", badgeColor: "gray", icon: File06, animate: false },
    error: { label: "Error", badgeColor: "error", icon: XCircle, animate: false },
};

// ---------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------

/** Whether a document's only error is a permanently failed export — a push failure, not a data
 *  problem. Such documents stay editable/exportable and get a "Retry export" action instead of
 *  a blocked Export (see failed-export-retry brief). */
export function isRetryableExportFailure(doc: HomeDocument): boolean {
    return !!doc.errorCode && !!doc.isExportFailed;
}

/** Whether a document can be toggled in selection mode (for delete or export).
 *  Excludes in-flight uploads and error documents — only exportable-eligible docs are selectable.
 *  Exception: a failed export is retryable, so those documents remain selectable. */
export function isDocumentSelectable(doc: HomeDocument): boolean {
    if (isRetryableExportFailure(doc)) return true;
    return doc.status !== "uploading" && doc.status !== "error" && !doc.errorCode;
}

/** Whether a document is eligible for batch export:
 *  must be a real document (not error), in end state, status "ready", "queued", or "exported",
 *  and no field validation errors. A document whose export failed (isExportFailed) is also
 *  eligible — retrying the export is the sanctioned recovery path.
 *  Note: We intentionally do NOT check hasAllRequiredFields here to match single-select behavior.
 *  Single-select computes required field validation live from documentDetail, ignoring the API's
 *  hasAllRequiredFields property. Batch mode should behave consistently. */
export function isDocumentExportable(doc: HomeDocument): boolean {
    if (doc.hasFieldErrors || !doc.isProcessingEndStateReached) return false;
    if (isRetryableExportFailure(doc)) return true;
    return !doc.errorCode && (doc.status === "ready" || doc.status === "queued" || doc.status === "exported");
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

export function formatFullDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

export function formatDisplayDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });
}
